// framework/client/components/RemoteDesktopViewer.js
import { ComponentBase } from "../core/ComponentBase.js";

// Use the ES module import path for noVNC
const NOVNC_CORE_RFB = '/public/lib/novnc/core/rfb.js';

export class RemoteDesktopViewer extends ComponentBase {
    static styles = `
        .remote-desktop-viewer {
            position: fixed;
            z-index: var(--ab-z-overlay); /* Higher than Bootstrap's modal backdrop */
            display: none; /* Hidden by default */
            flex-direction: column;
            border: 1px solid var(--bs-border-color);
            border-radius: var(--ab-radius-lg);
            box-shadow: var(--ab-shadow-lg);
            background-color: var(--bs-body-bg);
            /* Initial size and position */
            width: 891px;
            height: 600px;
            top: 10%;
            left: 15%;
        }
        .remote-desktop-viewer.maximized {
            top: 0; left: 0; width: 100vw; height: 100vh; border-radius: 0;
        }
        @media (max-width: 767px) and (orientation: portrait) {
            .remote-desktop-viewer {
                position: fixed !important;
                top: 0 !important;
                left: 100vw !important;
                width: 100vh !important;
                height: 100vw !important;
                transform: rotate(90deg) !important;
                transform-origin: top left !important;
                border-radius: 0 !important;
                border: none !important;
                z-index: var(--ab-z-critical);
            }
        }
        @media (max-width: 992px) and (orientation: landscape) {
            .remote-desktop-viewer {
                position: fixed !important;
                top: 0 !important;
                left: 0 !important;
                width: 100vw !important;
                height: 100vh !important;
                max-height: 100vh !important;
                max-width: 100vw !important;
                border-radius: 0 !important;
                border: none !important;
                z-index: var(--ab-z-critical);
                transform: none !important;
            }
        }
        .rd-header {
            display: flex;
            align-items: center;
            padding: 0.5rem 1rem;
            background-color: var(--bs-tertiary-bg);
            border-bottom: 1px solid var(--bs-border-color);
            cursor: move;
            flex-shrink: 0;
        }
        .rd-header-title {
            font-weight: var(--ab-weight-medium);
            flex-grow: 1;
        }
        .rd-header-controls {
            display: flex;
            gap: 0.5rem;
        }
        .rd-header-controls .btn {
            padding: 0.1rem 0.4rem;
            font-size: var(--ab-text-sm);
            transition: background var(--ab-t-fast), color var(--ab-t-fast);
        }
        .rd-header-controls .btn:focus-visible {
            outline: none;
            box-shadow: var(--ab-focus-ring);
        }
        .rd-body {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            overflow: hidden;
        }
        #rd-screen {
            flex-grow: 1;
            background-color: #000;
            position: relative;
            overflow: hidden;
        }
        #rd-status {
            padding: 0.25rem 0.75rem;
            background-color: var(--bs-dark);
            color: var(--bs-light);
            font-size: var(--ab-text-sm);
            flex-shrink: 0;
        }
        .rd-resizer {
            position: absolute;
            bottom: 0;
            right: 0;
            width: 15px;
            height: 15px;
            cursor: se-resize;
            background: repeating-linear-gradient(45deg, var(--bs-secondary-color), var(--bs-secondary-color) 2px, transparent 2px, transparent 4px);
            opacity: 0.5;
        }
        /* Styles for the new settings dropdown */
        .rd-settings-menu {
            padding: 0.75rem;
            min-width: 250px;
        }
        .rd-settings-menu .form-check {
            margin-bottom: 0.5rem;
        }
        .rd-settings-menu .btn {
            width: 100%;
            transition: background var(--ab-t-fast), color var(--ab-t-fast);
        }
        .rd-settings-menu .btn:focus-visible,
        .rd-settings-menu input[type="checkbox"]:focus-visible {
            outline: none;
            box-shadow: var(--ab-focus-ring);
        }
    `;

    constructor(config) {
        super(config);
        this.rfb = null;
        this.RFBClass = null; // To store the loaded noVNC RFB class
        this.isConnecting = false;
        this.isVisible = false;
        this.importMapAdded = false;

        // State for dragging and resizing
        this.dragInfo = { active: false, initialX: 0, initialY: 0, initialLeft: 0, initialTop: 0 };
        this.resizeInfo = { active: false, initialX: 0, initialY: 0, initialWidth: 0, initialHeight: 0 };
        this.preMaximizeState = { top: 0, left: 0, width: 0, height: 0 };

        this._boundHandleDisplayRequest = this.handleDisplayRequest.bind(this);
        this._boundDragMove = this._dragMove.bind(this);
        this._boundDragEnd = this._dragEnd.bind(this);
        this._boundResizeMove = this._resizeMove.bind(this);
        this._boundResizeEnd = this._resizeEnd.bind(this);

        this.applyStyles();
    }

    async initialize() {
        this.appClient.events.local.on('DISPLAY_REMOTE_SCREEN', this._boundHandleDisplayRequest);
        await super.initialize();
    }

    async handleDisplayRequest({ identityId, threadId }) {
        if (this.isConnecting || this.isVisible) {
            this.log.warn("Remote screen is already connecting or visible.");
            return;
        }
        this.isConnecting = true;
        this.show();
        this.updateStatus(this.t('Requesting connection token...'));

        try {
            const connectionDetails = await this.appClient.rpcClient.call('RemoteDesktopRpc.getVncConnectionDetails', { identityId, threadId });
            await this.connect(connectionDetails.url, connectionDetails.token);
        } catch (error) {
            this.log.error("Failed to get VNC connection details or connect:", error);
            this.updateStatus(`${this.t('Error')}: ${error.message}`, true);
            this.isConnecting = false;
        }
    }

    show() {
        if (!this.container) this.render();
        this.container.style.display = 'flex';
        this.isVisible = true;
    }

    hide() {
        if (this.container) this.container.style.display = 'none';
        this.isVisible = false;
        this.disconnect();
    }

    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'remote-desktop-viewer';
            this.container.id = `remote-desktop-viewer-${this.config.instanceId}`;
            this.container.innerHTML = `
                <div class="rd-header">
                    <span class="rd-header-title">${this.t('Remote Desktop')}</span>
                    <div class="rd-header-controls">
                        <div class="dropdown">
                            <button class="btn btn-sm btn-outline-secondary" type="button" data-bs-toggle="dropdown" aria-expanded="false" title="${this.t('Settings')}">
                                <i class="bi bi-gear-fill"></i>
                            </button>
                            <div class="dropdown-menu dropdown-menu-end rd-settings-menu">
                                <div class="form-check form-switch">
                                    <input class="form-check-input" type="checkbox" role="switch" id="rd-scale-viewport" checked>
                                    <label class="form-check-label" for="rd-scale-viewport">${this.t('Scale to Fit')}</label>
                                </div>
                                <div class="form-check form-switch">
                                    <input class="form-check-input" type="checkbox" role="switch" id="rd-view-only">
                                    <label class="form-check-label" for="rd-view-only">${this.t('View Only')}</label>
                                </div>
                                <hr class="my-2">
                                <button id="rd-ctrl-alt-del" class="btn btn-sm btn-outline-secondary" type="button">${this.t('Send Ctrl+Alt+Del')}</button>
                            </div>
                        </div>
                        <button id="rd-maximize-btn" class="btn btn-sm btn-outline-secondary" title="${this.t('Maximize')}"><i class="bi bi-square"></i></button>
                        <button id="rd-fullscreen-btn" class="btn btn-sm btn-outline-secondary" title="${this.t('Fullscreen')}"><i class="bi bi-arrows-fullscreen"></i></button>
                        <button id="rd-close-btn" class="btn btn-sm btn-outline-danger" title="${this.t('Close')}"><i class="bi bi-x-lg"></i></button>
                    </div>
                </div>
                <div class="rd-body">
                    <div id="rd-screen"></div>
                    <div id="rd-status" class="p-2 bg-dark text-white small">${this.t('Ready')}</div>
                </div>
                <div class="rd-resizer"></div>
            `;
            document.body.appendChild(this.container);
            this._attachEventListeners();
        }
        return this.container;
    }

    _attachEventListeners() {
        const header = this.container.querySelector('.rd-header');
        const resizer = this.container.querySelector('.rd-resizer');

        header.addEventListener('mousedown', this._dragStart.bind(this));
        resizer.addEventListener('mousedown', this._resizeStart.bind(this));

        this.container.querySelector('#rd-close-btn').addEventListener('click', () => this.hide());
        this.container.querySelector('#rd-maximize-btn').addEventListener('click', () => this.toggleMaximize());
        this.container.querySelector('#rd-fullscreen-btn').addEventListener('click', () => this.toggleFullscreen());

        const settingsMenu = this.container.querySelector('.rd-settings-menu');
        settingsMenu.addEventListener('click', (e) => e.stopPropagation());

        this.container.querySelector('#rd-ctrl-alt-del').addEventListener('click', () => this.rfb?.sendCtrlAltDel());
        this.container.querySelector('#rd-view-only').addEventListener('change', (e) => { if (this.rfb) this.rfb.viewOnly = e.target.checked; });
        this.container.querySelector('#rd-scale-viewport').addEventListener('change', (e) => { if (this.rfb) this.rfb.scaleViewport = e.target.checked; });
    }


    async _loadNoVNC() {
        if (this.RFBClass) return;
        this.updateStatus(this.t('Loading remote desktop client...'));
        try {
            const rfbModule = await import(NOVNC_CORE_RFB);
            this.RFBClass = rfbModule.default;
            if (!this.RFBClass) throw new Error('RFB class not found in noVNC module');
            this.log.info("noVNC RFB module loaded successfully");
        } catch (err) {
            this.log.error("Failed to load noVNC RFB module:", err);
            this.updateStatus(`${this.t('Error')}: ${this.t('Failed to load remote desktop client.')}`, true);
            throw err;
        }
    }

    async connect(url, token) {
        try {
            await this._loadNoVNC();
            const screen = this.container.querySelector('#rd-screen');
            const fullUrl = `${url}?token=${encodeURIComponent(token)}`;
            
            this.updateStatus(this.t('Connecting...'));
            if (this.rfb) this.rfb.disconnect();

            this.rfb = new this.RFBClass(screen, fullUrl, { credentials: { password: 'vncpassword' } });
            this.rfb.viewOnly = this.container.querySelector('#rd-view-only').checked;
            this.rfb.scaleViewport = this.container.querySelector('#rd-scale-viewport').checked;

            this.rfb.addEventListener("connect", () => {
                this.isConnecting = false;
                this.updateStatus(this.t('Connected'));
            });
            this.rfb.addEventListener("disconnect", (e) => {
                this.isConnecting = false;
                const message = e.detail.clean ? this.t('Disconnected') : this.t('Connection lost');
                this.updateStatus(message, !e.detail.clean);
                this.rfb = null;
            });
            this.rfb.addEventListener("desktopname", (e) => {
                this.container.querySelector('.rd-header-title').textContent = `${this.t('Remote')}: ${e.detail.name}`;
            });
        } catch (error) {
            this.log.error("Failed to initiate connection:", error);
            this.updateStatus(`${this.t('Connection failed')}: ${error.message}`, true);
            this.isConnecting = false;
        }
    }

    disconnect() {
        if (this.rfb) {
            this.log.info("Disconnecting from remote desktop session.");
            this.rfb.disconnect();
            this.rfb = null;
        }
        this.isConnecting = false;
        if (this.container) {
            this.updateStatus(this.t('Disconnected'));
            this.container.querySelector('.rd-header-title').textContent = this.t('Remote Desktop');
        }
    }

    updateStatus(message, isError = false) {
        const statusEl = this.container?.querySelector('#rd-status');
        if (statusEl) {
            statusEl.textContent = message;
            statusEl.classList.toggle('bg-danger', isError);
            statusEl.classList.toggle('bg-dark', !isError);
        }
    }

    _dragStart(e) {
        if (this.container.classList.contains('maximized')) return;
        this.dragInfo.active = true;
        this.dragInfo.initialX = e.clientX;
        this.dragInfo.initialY = e.clientY;
        this.dragInfo.initialLeft = this.container.offsetLeft;
        this.dragInfo.initialTop = this.container.offsetTop;
        document.addEventListener('mousemove', this._boundDragMove);
        document.addEventListener('mouseup', this._boundDragEnd);
    }
    _dragMove(e) {
        if (!this.dragInfo.active) return;
        const dx = e.clientX - this.dragInfo.initialX;
        const dy = e.clientY - this.dragInfo.initialY;
        this.container.style.left = `${this.dragInfo.initialLeft + dx}px`;
        this.container.style.top = `${this.dragInfo.initialTop + dy}px`;
    }
    _dragEnd() {
        this.dragInfo.active = false;
        document.removeEventListener('mousemove', this._boundDragMove);
        document.removeEventListener('mouseup', this._boundDragEnd);
    }

    _resizeStart(e) {
        this.resizeInfo.active = true;
        this.resizeInfo.initialX = e.clientX;
        this.resizeInfo.initialY = e.clientY;
        this.resizeInfo.initialWidth = this.container.offsetWidth;
        this.resizeInfo.initialHeight = this.container.offsetHeight;
        document.addEventListener('mousemove', this._boundResizeMove);
        document.addEventListener('mouseup', this._boundResizeEnd);
    }
    _resizeMove(e) {
        if (!this.resizeInfo.active) return;
        const dw = e.clientX - this.resizeInfo.initialX;
        const dh = e.clientY - this.resizeInfo.initialY;
        this.container.style.width = `${this.resizeInfo.initialWidth + dw}px`;
        this.container.style.height = `${this.resizeInfo.initialHeight + dh}px`;
    }
    _resizeEnd() {
        this.resizeInfo.active = false;
        document.removeEventListener('mousemove', this._boundResizeMove);
        document.removeEventListener('mouseup', this._boundResizeEnd);
    }

    toggleMaximize() {
        const isMaximized = this.container.classList.toggle('maximized');
        const icon = this.container.querySelector('#rd-maximize-btn i');
        if (isMaximized) {
            this.preMaximizeState = { top: this.container.style.top, left: this.container.style.left, width: this.container.style.width, height: this.container.style.height };
            this.container.style.top = this.container.style.left = this.container.style.width = this.container.style.height = '';
            icon.className = 'bi bi-files';
        } else {
            Object.assign(this.container.style, this.preMaximizeState);
            icon.className = 'bi bi-square';
        }
    }

    toggleFullscreen() {
        if (!document.fullscreenElement) {
            this.container.requestFullscreen().catch(err => {
                this.log.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
            });
        } else {
            document.exitFullscreen();
        }
    }

    destroy() {
        this.appClient.events.local.off('DISPLAY_REMOTE_SCREEN', this._boundHandleDisplayRequest);
        this.disconnect();
        this.container?.remove();
        super.destroy();
    }
}